/**
 * PatientAntecedentsTab — CIM-10 coded tag manager for the patient drawer.
 * Renders inside <PatientDetailDrawer> when the "Antécédents" tab is active.
 */
import React from "react";
import { toast } from "sonner";
import { Info, Search, X } from "lucide-react";
import { CIM10_CATALOG } from "./data";

export function PatientAntecedentsTab({ patient }) {
  const initial = React.useMemo(() => {
    if (Array.isArray(patient.antecedentsCoded)) return patient.antecedentsCoded;
    // Fallback: convert plain strings into loose tags without a real code.
    return (patient.antecedents || []).map((a) => ({ code: "—", label: a, since: "" }));
  }, [patient]);
  const [tags, setTags] = React.useState(initial);
  const [q, setQ] = React.useState("");
  const [showMenu, setShowMenu] = React.useState(false);

  const matches = q.trim()
    ? CIM10_CATALOG.filter((c) => c.code.toLowerCase().includes(q.toLowerCase()) || c.label.toLowerCase().includes(q.toLowerCase())).slice(0, 8)
    : CIM10_CATALOG.slice(0, 8);

  const addTag = (entry) => {
    if (tags.some((t) => t.code === entry.code && t.label === entry.label)) {
      toast.info("Cet antécédent est déjà présent.");
      return;
    }
    setTags([...tags, { ...entry, since: new Date().getFullYear().toString() }]);
    setQ("");
    setShowMenu(false);
    toast.success(`Antécédent ajouté : ${entry.code} — ${entry.label} (sandbox).`);
  };

  const removeTag = (idx) => {
    setTags(tags.filter((_, i) => i !== idx));
    toast.success("Antécédent retiré (sandbox).");
  };

  return (
    <div className="space-y-4" data-testid="patient-tab-content-antecedents">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-[12px] text-blue-900 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Antécédents <strong>codés CIM-10</strong> (WHO / ATIH) — remplace la saisie libre, garantit l&apos;interopérabilité avec le DMP, l&apos;épidémiologie, et le calcul automatique des indicateurs ROSP.
        </div>
      </div>

      {/* Search + tags list */}
      <div>
        <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold block mb-1.5">Ajouter un antécédent</label>
        <div className="relative">
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setShowMenu(true); }}
            onFocus={() => setShowMenu(true)}
            placeholder="Rechercher par code (I10) ou libellé (HTA, diabète…)"
            data-testid="antecedents-search"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] text-slate-900 bg-white pr-9"
          />
          <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          {showMenu && matches.length > 0 && (
            <ul className="absolute z-30 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg">
              {matches.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => addTag(c)}
                    className="w-full text-left px-3 py-2 text-[13px] text-slate-800 hover:bg-blue-50 flex items-start gap-3"
                    data-testid={`antecedents-suggestion-${c.code}`}
                  >
                    <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 shrink-0">{c.code}</span>
                    <span>{c.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-1 text-[11px] text-slate-400">Cliquez sur une suggestion pour l&apos;ajouter. Format CIM-10 (Classification internationale des maladies).</div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold mb-2">Antécédents actuels ({tags.length})</div>
        {tags.length === 0 ? (
          <div className="text-[13px] text-slate-500 italic">Aucun antécédent codé.</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="antecedents-tags">
            {tags.map((t, i) => (
              <span
                key={`${t.code}-${i}`}
                className="inline-flex items-center gap-2 pl-2 pr-1 py-1 rounded-full bg-[#F0F9FF] border border-[#BAE6FD] text-[#0F2B5B] text-[12.5px]"
                data-testid={`antecedents-tag-${i}`}
              >
                <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-700">{t.code}</span>
                <span className="font-medium">{t.label}</span>
                {t.since && <span className="text-[10.5px] text-slate-500">· {t.since}</span>}
                <button
                  onClick={() => removeTag(i)}
                  className="w-5 h-5 rounded-full hover:bg-red-100 hover:text-red-600 text-slate-400 grid place-items-center ml-0.5"
                  aria-label="Retirer"
                  data-testid={`antecedents-remove-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
